import {
  ConnectableElement,
  Connector,
  ConnectorElement,
  ConnectorInstance,
  ConnectorParameters,
  Datapoint,
  DefaultDatapointTypes,
  EventDatapointParameters,
  InlineConnectableElement,
  JSAPIDatapointParameters,
  JSONObject,
  JSONValue,
  TYPE,
} from '@olive/olive-types';
import * as EventEmitter from 'events';
import APIManager from '../APIManager';
import ConnectivityHolder, { ConnectivityDatapoint } from '../ConnectivityHolder';
import ConnectivityManager from '../ConnectivityManager';
import { getDatapointFromConnectorElement } from '../../utils/ConnectivityFinder';
import AbstractDispatcher from './AbstractDispatcher';
import WindowDispatcher from './WindowDispatcher';
import { isConnectorDefinition, isConnectorInstance, isConnectorTask, isDatapointDefinition, isDatapointInstance } from '@olive/types-util';
import { replacePlaceholdersLocal, replacePlaceholdersObjectLocal } from '@olive/placeholder-util-browser';
import ENV_VARS from '../EnvironmentVariables';

type DispatchDatapointProps = {
  id: string;
  input?: object;
  parameters?: {
    componentInstanceID: string;
  };
};

const SUPPORTED_TYPES = [
  'olive',
  'event',
  'jsAPI',
  'windowmessage',
  'broadcast',
];

export const CLIENT_DISPATCHER_ID = 'client-dispatcher';

export default class ClientDispatcher extends AbstractDispatcher {
  eventManager: EventEmitter;
  apiManager: APIManager;
  connectivityManager: ConnectivityManager;
  // broadcastDispatcher: BroadcastDispatcher;
  windowDispatcher: WindowDispatcher;

  constructor(
    connectivityHolder: ConnectivityHolder,
    eventManager: EventEmitter,
    apiManager: APIManager,
    connectivityManager: ConnectivityManager
  ) {
    super(connectivityHolder);
    this.eventManager = eventManager;
    this.apiManager = apiManager;
    this.connectivityManager = connectivityManager;
    this.windowDispatcher = new WindowDispatcher(
      connectivityHolder,
      connectivityManager
    );
  }

  async dispatch({ id, task, input }: { id: string, task?: ConnectableElement | InlineConnectableElement, input?: object }): Promise<unknown> {
    let datapoint: ConnectableElement;
    if (task) {
      datapoint = task as ConnectableElement;
    }
    else {
      datapoint = this.connectivityHolder.get(id) as ConnectableElement;
    }

    console.log(datapoint, datapoint.type);
    switch (datapoint.type) {
      case TYPE.CONNECTOR:
      case 'connectorInstance':
        return await this.dispatchConnector(
          datapoint as Connector,
          input
        );
      case TYPE.DATAPOINT:
        return await this.dispatchDatapoint(
          datapoint as ConnectivityDatapoint,
          input
        );
      case TYPE.WORKFLOW:
        return await this.connectivityManager.invoke({
          id: datapoint.id,
          task: datapoint,
          input,
        });
      default:
        console.warn(`Datapoint Type ${datapoint.type} not supported!`);
        return `Datapoint Type ${datapoint.type} not supported`;
    }
  }

  canDispatch(props: { id: string }): boolean {
    try {
      const { datapoint, type } = this.connectivityHolder.get(props.id) as Datapoint;
      if ([TYPE.CONNECTOR, 'connectorInstance'].includes(type)) {
        return true;
      }
      const datapointType = datapoint.type;
      return SUPPORTED_TYPES.includes(datapointType);
    } catch {
      return false;
    }
  }

  canDispatchType(type: string): boolean {
    return SUPPORTED_TYPES.includes(type);
  }

  /**
   * Gets the connector configuration from a connector
   */
  private getConnectorConfig(connector: Connector | ConnectorInstance): ConnectorParameters | null {
    if (isConnectorDefinition(connector)) {
      return connector.connector;
    }
    else if (isConnectorInstance(connector)) {
      return connector;
    }
    else {
      console.error('Unknown connector type: ', connector);
      return null;
    }
  }

  /**
   * Extracts parameters from a connector element
   */
  private extractParameters(
    datapoint: ConnectorParameters['elements'][0],
    input?: object
  ): { ref: string | null, parameters: JSONObject | null } {
    let ref: string = null;
    let parameters: JSONObject = null;

    if (isConnectorTask(datapoint) || isDatapointInstance(datapoint)) {
      if ('ref' in datapoint) {
        ref = datapoint.ref;
      }
      if (input && ref && input[ref]) {
        parameters = { ...datapoint.parameters, ...input[ref] };
      }
      else if (datapoint.parameters) {
        parameters = datapoint.parameters;
      }
      else if (input) {
        parameters = { ...input };
      }
    }

    return { ref, parameters };
  }

  /**
   * Checks if the datapoint should skip processing and just pass input
   */
  private shouldSkipProcessing(
    resolvedConnectivity: ConnectableElement | null,
    index: number
  ): boolean {
    return !resolvedConnectivity || (
      isDatapointDefinition(resolvedConnectivity) &&
      index === 0 &&
      (
        resolvedConnectivity.datapoint.type === DefaultDatapointTypes.EVENT ||
        resolvedConnectivity.datapoint.type === DefaultDatapointTypes.WINDOW_MESSAGE
      )
    );
  }


  async dispatchConnector(
    connector: Connector | ConnectorInstance,
    input?: object
  ): Promise<unknown> {
    const connectorConfig = this.getConnectorConfig(connector);
    if (!connectorConfig) {
      return null;
    }

    console.log('connectorConfig: ', connectorConfig);
    // Single object for storing results by ref and for JSONata evaluation
    const resultMap: JSONObject = {};
    let lastResult = null;

    for (let i = 0; i < connectorConfig.elements.length; i++) {
      const element = connectorConfig.elements[i];
      const { ref, parameters } = this.extractParameters(element, input);
      const resolvedConnectivity = getDatapointFromConnectorElement(element, this.connectivityHolder);

      // If no datapoint that should be dispatched (e.g. event as first element in connector) just take the input as result
      if (this.shouldSkipProcessing(resolvedConnectivity, i)) {
        if (ref) {
          resultMap[ref] = input as JSONValue;
        }
        continue;
      }

      const isDatapointElement = (isConnectorTask(resolvedConnectivity as ConnectorElement) || isDatapointInstance(resolvedConnectivity) || isDatapointDefinition(resolvedConnectivity))
      const placeholderParams = {
        input: lastResult as JSONObject,
        root: typeof (input) == 'object' ? input as JSONObject : {},
        ref: resultMap,
        params: parameters,
        env: ENV_VARS.getAll()
      };

      const fullPayload = { ...parameters };

      const resolvedDatapointTask = isDatapointElement ?
        await replacePlaceholdersLocal(resolvedConnectivity as Datapoint, placeholderParams) :
        resolvedConnectivity;
      const resolvedPayload = await replacePlaceholdersObjectLocal(fullPayload, placeholderParams);

      console.log('invoking endpoint with----------', resolvedPayload);

      const result = await this.connectivityManager.invoke({
        id: resolvedConnectivity.id,
        task: resolvedDatapointTask,
        input: resolvedPayload
      });

      lastResult = result;

      if (ref) {
        resultMap[ref] = result as JSONValue;
      }
    }

    return lastResult;
  }

  async dispatchDatapoint(
    datapoint: ConnectivityDatapoint,
    input?: object,
    props?: DispatchDatapointProps
  ): Promise<unknown> {

    const placeholderParams = {
      input: input as JSONObject,
      params: datapoint.datapoint.parameters,
      env: ENV_VARS.getAll()
    };

    datapoint = await replacePlaceholdersLocal(datapoint, placeholderParams) as ConnectivityDatapoint;
    input = await replacePlaceholdersObjectLocal(input, placeholderParams);

    if (datapoint.datapoint.type === DefaultDatapointTypes.EVENT) {
      const eventDatapoint = datapoint.datapoint as EventDatapointParameters;
      return this.dispatchEvent(eventDatapoint.event.topic, input);
    }
    if (datapoint.datapoint.type === DefaultDatapointTypes.JS_API) {
      const parameters = props?.parameters;
      let componentInstanceID = '';
      if (parameters) {
        componentInstanceID = parameters.componentInstanceID;
      } else {
        componentInstanceID = datapoint.parameters?.componentInstanceID as string ||
          (input && 'componentInstanceID' in input && input?.componentInstanceID) as string;
      }
      const { ...datapointRest } = datapoint.parameters || {};
      const { ...propsRest } = parameters || {};
      const jsAPIDatapoint = datapoint.datapoint as JSAPIDatapointParameters;
      if (!componentInstanceID || componentInstanceID.length === 0) {
        componentInstanceID = jsAPIDatapoint.jsAPI.parameters?.componentInstanceID as string;
      }
      return this.dispatchJSAPI({
        apiName: jsAPIDatapoint.jsAPI.api,
        componentInstanceID,
        input,
        props: { ...datapointRest, ...propsRest },
      });
    }
    if (this.windowDispatcher.canDispatch({ id: datapoint.id, datapoint })) {
      return await this.windowDispatcher.dispatch({
        id: datapoint.id,
        task: datapoint,
        input,
      });
    }
    console.warn(
      'Currently unsupported datapointtype for client side: ' + datapoint.datapoint.type
    );
    return null;
  }


  dispatchEvent(topic: string, params: object) {
    this.eventManager.emit(topic, params);
  }

  dispatchJSAPI({
    apiName,
    componentInstanceID,
    input,
    props,
  }: {
    apiName: string;
    componentInstanceID: string;
    input: object;
    props: object;
  }) {
    const apiID = componentInstanceID + '-' + apiName;
    const api = this.apiManager.get(apiID);
    if (api) {
      return api({
        ...props,
        ...input
      });
    } else {
      console.error('Could not find api with id ' + apiID);
    }
  }
}
